/**
 * HomeworkContinuityBridge — Phase 3 / V1 (Feb 28, 2026).
 *
 * ONE calm "Last time → This time" continuity bridge on the
 * canonical learner home. Turns homework framing into lesson
 * continuation rather than a disconnected task pile.
 *
 * Phase 5 (Feb 28, 2026) — Operational Continuity Convergence v2:
 *   • Header label moved from "Homework continuity" to
 *     "Where we left off" — same surface, calmer wording, no
 *     productivity-system tilt. The bridge already lives inside
 *     the "Next lesson & homework" section, so the section header
 *     still gives operational context without the bridge having
 *     to repeat it.
 *   • Accepts `suppressLastTime` prop. When the engine's
 *     `from_your_last_lesson` slot is populated, that slot owns the
 *     backward-looking psychological question and the bridge's
 *     "Last time:" row defers silently. The anchor sentence keeps
 *     its forward-only variant. "This time" still renders — it is
 *     the unique forward-continuity signal this bridge owns.
 *
 * Doctrine pins baked in:
 *
 *   • Reuses ONE existing endpoint: GET /api/learner-framework/me
 *     (which already powers the framework strip + recent gain).
 *     NO new backend route, NO new collection.
 *
 *   • Surface shape, in order:
 *       1. "Last time"     — last signalled skill_progress row title
 *       2. "This time"     — current_stage.next_lesson_title
 *       3. continuation anchor — short calm sentence stitched from
 *          the two above (server data, no AI, no generation)
 *
 *     The optional reinforcement/action and optional understanding
 *     reminder lines are HELD for V1 — they would require a
 *     generation source we are explicitly not building yet.
 *
 *   • Silent rendering: when BOTH "Last time" and "This time" are
 *     missing — or when the fetch fails — the bridge returns null.
 *     No fallback copy, no "complete your profile" nag, no fake
 *     homework.
 *
 *   • No gamification language anywhere. No streaks. No XP.
 *     No achievements. No percentages. No scoring.
 *
 *   • This surface is FORWARD-LOOKING continuity, not primary
 *     orientation — Phase 1's `where_you_are` suppression already
 *     owns orientation. The bridge uses different wording
 *     ("Last time / This time") to stay clear of duplication.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import FocusVisual from "./FocusVisual";

const API = process.env.REACT_APP_BACKEND_URL;

function extractLastTime(framework) {
  const rows = Array.isArray(framework?.skill_progress)
    ? framework.skill_progress
    : [];
  if (!rows.length) return null;
  // Most recently signalled row that has a real title.
  const sorted = [...rows]
    .filter((r) => r && (r.title || "").trim() && r.last_signal_at)
    .sort((a, b) => String(b.last_signal_at).localeCompare(String(a.last_signal_at)));
  const top = sorted[0];
  return top ? top.title.trim() : null;
}

function extractThisTime(framework) {
  const t = (framework?.current_stage?.next_lesson_title || "").trim();
  return t || null;
}

export default function HomeworkContinuityBridge({
  // Phase 5 deference: when the engine's `from_your_last_lesson`
  // slot is populated above, the bridge's "Last time:" row is
  // silent — engine slot owns the backward question. "This time"
  // and the forward anchor still render. Default false → original
  // Phase 3 behaviour preserved.
  suppressLastTime = false,
} = {}) {
  const [framework, setFramework] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/learner-framework/me`,
          { withCredentials: true },
        );
        if (!cancelled) setFramework(data || null);
      } catch {
        // Silent — calm doctrine: missing data → no DOM.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const rawLastTime = extractLastTime(framework);
  const thisTime = extractThisTime(framework);
  // Phase 5: defer the backward "Last time" row when the engine
  // slot owns that question above. Keep `thisTime` intact.
  const lastTime = suppressLastTime ? null : rawLastTime;

  // Calm doctrine: silent when neither anchor is available.
  if (!lastTime && !thisTime) return null;

  // Continuation anchor — short calm sentence stitched from the
  // two anchors. No AI, no generation, no analysis.
  let anchor = "";
  if (lastTime && thisTime) {
    anchor = `We'll build on ${lastTime} as we move into ${thisTime}.`;
  } else if (thisTime) {
    anchor = `Next, we'll work on ${thisTime}.`;
  } else if (lastTime) {
    anchor = `We were last working on ${lastTime}.`;
  }

  return (
    <section
      data-testid="homework-continuity-bridge"
      aria-labelledby="hwcb-title"
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 shadow-sm"
    >
      <p
        id="hwcb-title"
        className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-slate-400 mb-2"
      >
        Where we left off
      </p>
      <div className="space-y-1 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
        {lastTime && (
          <p data-testid="homework-continuity-last-time">
            <span className="font-bold text-slate-500 dark:text-slate-400">
              Last time:
            </span>{" "}
            {lastTime}
          </p>
        )}
        {thisTime && (
          <p data-testid="homework-continuity-this-time">
            <span className="font-bold text-slate-500 dark:text-slate-400">
              This time:
            </span>{" "}
            {thisTime}
          </p>
        )}
        {anchor && (
          <p
            data-testid="homework-continuity-anchor"
            className="text-xs text-slate-500 dark:text-slate-400 pt-1"
          >
            {anchor}
          </p>
        )}
        {/* Phase 4 — visual support mounted INSIDE the bridge, NOT
            as a separate card. Silent when no safe focus match. */}
        <FocusVisual focus={thisTime || lastTime} />
      </div>
    </section>
  );
}

// Exported pure helpers for doctrine pinning.
export { extractLastTime, extractThisTime };
